import { useState, type FormEvent } from 'react';
import { Github, Copy, Check, Trash2, Save, Loader2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  usePlatformOAuth,
  useSaveOAuthProvider,
  useDisableOAuthProvider,
  type OAuthProviderStatus,
} from '@/hooks/usePlatform';
import { ApiError } from '@/lib/api-client';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

export function OAuthSettingsPage() {
  const { data, isPending } = usePlatformOAuth();

  if (isPending) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!data) return <div className="text-destructive">Veri yüklenemedi</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5" /> OAuth Sağlayıcılar
        </h1>
        <p className="text-muted-foreground text-sm">
          Kullanıcıların Google ve GitHub ile giriş yapabilmesi için provider credentials'ları.
          Secret'lar AES-GCM ile şifrelenmiş şekilde D1'de saklanır.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ProviderCard
          provider="github"
          label="GitHub"
          icon={<Github className="w-5 h-5" />}
          callbackUrl={data.callback_urls.github}
          status={data.providers.find((p) => p.provider === 'github')!}
          docsUrl="https://github.com/settings/developers"
          docsLabel="GitHub OAuth Apps"
          helperHtml={
            <>
              <p>
                <a href="https://github.com/settings/developers" target="_blank" rel="noreferrer" className="text-primary underline-offset-4 hover:underline">
                  GitHub Developer Settings
                </a>
                {' '}→ OAuth Apps → New OAuth App
              </p>
              <p>Authorization callback URL olarak aşağıdaki callback URL'i yapıştır.</p>
            </>
          }
        />
        <ProviderCard
          provider="google"
          label="Google"
          icon={<GoogleIcon className="w-5 h-5" />}
          callbackUrl={data.callback_urls.google}
          status={data.providers.find((p) => p.provider === 'google')!}
          docsUrl="https://console.cloud.google.com/apis/credentials"
          docsLabel="Google Cloud Console"
          helperHtml={
            <>
              <p>
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-primary underline-offset-4 hover:underline">
                  Google Cloud Console
                </a>
                {' '}→ Credentials → Create Credentials → OAuth client ID → Web application
              </p>
              <p>Authorized redirect URIs'a callback URL'i ekle.</p>
            </>
          }
        />
      </div>
    </div>
  );
}

function ProviderCard({
  provider,
  label,
  icon,
  callbackUrl,
  status,
  docsUrl,
  docsLabel,
  helperHtml,
}: {
  provider: 'github' | 'google';
  label: string;
  icon: React.ReactNode;
  callbackUrl: string;
  status: OAuthProviderStatus;
  docsUrl: string;
  docsLabel: string;
  helperHtml: React.ReactNode;
}) {
  const save = useSaveOAuthProvider();
  const disable = useDisableOAuthProvider();
  const confirm = useConfirm();

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    try {
      await save.mutateAsync({ provider, client_id: clientId, client_secret: clientSecret });
      setClientId('');
      setClientSecret('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  const onCopy = async () => {
    await navigator.clipboard.writeText(callbackUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onDisable = async () => {
    const ok = await confirm({
      title: `${label} bağlantısını kaldır?`,
      description: 'Kullanıcılar bu sağlayıcı ile giriş yapamaz. Mevcut hesaplar etkilenmez.',
      confirmText: 'Kaldır',
      danger: true,
    });
    if (!ok) return;
    await disable.mutateAsync(provider);
  };

  const configured = status.configured && status.enabled;
  void docsUrl;
  void docsLabel;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <CardTitle className="text-base">{label}</CardTitle>
            <StatusBadge configured={configured} />
          </div>
        </div>
        <CardDescription className="space-y-1 text-xs">{helperHtml}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Callback URL</Label>
          <div className="flex gap-2">
            <code
              className="flex-1 min-w-0 font-mono text-[11px] bg-muted px-2 py-1.5 rounded truncate"
              title={callbackUrl}
            >
              {callbackUrl}
            </code>
            <Button size="sm" variant="outline" type="button" onClick={onCopy}>
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        {configured && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs flex items-center justify-between">
            <span>
              Client ID: <code className="font-mono">{status.client_id_prefix}</code>
            </span>
            <Button size="sm" variant="ghost" onClick={onDisable} disabled={disable.isPending}>
              <Trash2 className="w-3.5 h-3.5" /> Bağlantıyı kaldır
            </Button>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${provider}-id`}>Client ID</Label>
            <Input
              id={`${provider}-id`}
              required
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={configured ? '(yeni Client ID gir ya da eskisi kullanılır)' : 'Client ID'}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${provider}-secret`}>Client Secret</Label>
            <Input
              id={`${provider}-secret`}
              type="password"
              required
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={configured ? '••••••••••••••••' : 'Client Secret'}
            />
          </div>
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5">{error}</div>
          )}
          {success && (
            <div className="text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded px-2 py-1.5">
              Kaydedildi ✓
            </div>
          )}
          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {configured ? 'Güncelle' : 'Kaydet'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ configured }: { configured: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        configured
          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {configured ? 'Yapılandırıldı' : 'Yok'}
    </span>
  );
}
